import React, { useState, useRef } from 'react';
import { PhotoIcon, XMarkIcon, CloudArrowUpIcon } from '@heroicons/react/24/outline';
import { ImageUploadService } from '../services/imageUploadService';

interface ImageUploadProps {
  currentImageUrl?: string;
  onImageUploaded: (url: string) => void;
  onImageRemoved?: () => void;
  folder: 'candidates' | 'admins' | 'members';
  maxSize?: number; // en MB
  className?: string;
  disabled?: boolean;
}

const ImageUpload: React.FC<ImageUploadProps> = ({
  currentImageUrl,
  onImageUploaded,
  onImageRemoved,
  folder,
  maxSize = 5,
  className = '',
  disabled = false
}) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [preview, setPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setError('');
    setUploading(true);

    try {
      // Redimensionner l'image si nécessaire
      const resizedFile = await ImageUploadService.resizeImage(file, 400, 400, 0.8);
      
      // Générer un nom de fichier unique
      const fileName = ImageUploadService.generateUniqueFileName(resizedFile.name, folder);
      
      // Upload vers Firebase Storage
      const result = await ImageUploadService.uploadImage(resizedFile, fileName);
      
      if (result.success && result.url) {
        onImageUploaded(result.url);
        setPreview(result.url);
      } else {
        setError(result.error || 'Erreur lors de l\'upload');
      }
    } catch (error: any) {
      setError(error.message || 'Erreur lors du traitement de l\'image');
    } finally {
      setUploading(false);
      // Réinitialiser l'input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleRemoveImage = async () => {
    if (currentImageUrl) {
      // Supprimer l'ancienne image de Firebase Storage
      await ImageUploadService.deleteImage(currentImageUrl);
    }
    
    setPreview(null);
    onImageRemoved?.();
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    if (disabled) return;

    const files = event.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (file.type.startsWith('image/')) {
        // Simuler un changement d'input
        const dataTransfer = new DataTransfer();
        dataTransfer.items.add(file);
        if (fileInputRef.current) {
          fileInputRef.current.files = dataTransfer.files;
          handleFileSelect({ target: { files: dataTransfer.files } } as any);
        }
      } else {
        setError('Veuillez sélectionner un fichier image');
      }
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
  };

  const displayImage = preview || currentImageUrl;

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Zone d'upload */}
      <div
        className={`
          relative border-2 border-dashed rounded-lg p-6 text-center transition-colors duration-200
          ${disabled 
            ? 'border-gray-200 bg-gray-50 cursor-not-allowed' 
            : 'border-gray-300 hover:border-blue-400 cursor-pointer'
          }
          ${error ? 'border-red-300 bg-red-50' : ''}
        `}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onClick={() => !disabled && fileInputRef.current?.click()}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileSelect}
          className="hidden"
          disabled={disabled}
        />

        {displayImage ? (
          <div className="space-y-3">
            <div className="relative inline-block">
              <img
                src={displayImage}
                alt="Aperçu"
                className="w-24 h-24 rounded-full object-cover mx-auto border-2 border-gray-200"
              />
              {!disabled && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleRemoveImage();
                  }}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                >
                  <XMarkIcon className="w-4 h-4" />
                </button>
              )}
            </div>
            <div className="text-sm text-gray-600">
              {uploading ? 'Upload en cours...' : 'Cliquez pour changer'}
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex justify-center">
              {uploading ? (
                <div className="w-12 h-12 border-4 border-blue-200 border-t-blue-600 rounded-full animate-spin"></div>
              ) : (
                <PhotoIcon className="w-12 h-12 text-gray-400" />
              )}
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900">
                {uploading ? 'Upload en cours...' : 'Ajouter une photo'}
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Glissez-déposez ou cliquez pour sélectionner
              </p>
              <p className="text-xs text-gray-400 mt-1">
                JPG, PNG, WebP • Max {maxSize}MB
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Message d'erreur */}
      {error && (
        <div className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-2">
          {error}
        </div>
      )}

      {/* Bouton d'upload alternatif */}
      {!displayImage && !uploading && (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled}
          className="w-full flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <CloudArrowUpIcon className="w-4 h-4 mr-2" />
          Sélectionner une image
        </button>
      )}
    </div>
  );
};

export default ImageUpload;
